<template>
  <v-card
    elevation="0"
    light
    class="html2pdf__page"
    style="width: 210mm; min-height: 296mm; padding: 10mm; break-before: page"
  >
    <div class="d-flex gap-3">
      <div
        :style="{
          backgroundImage: `url('/img/logo/logo-dark.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPositionX: 'center',
          backgroundPositionY: 'start',
          width: '100px',
          height: '50px',
        }"
      ></div>
      <div>
        <h4 class="mb-0 lh-5">Ingresso #{{ ticket?.id | shortId }}</h4>
        <p class="mb-0 text-caption font-weight-bold">{{ ticket?.id }}</p>
      </div>
    </div>

    <v-card v-if="ticket.TicketBlock" outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Evento</span>
      <h5 class="mb-0 lh-3">{{ ticket.TicketBlock.TicketGroup.Party.name }}</h5>
      <span class="text-caption">{{
        ticket.TicketBlock.TicketGroup.Party?.date | date("DD/MM/YYYY HH:mm")
      }}</span>
      <v-divider class="my-2"></v-divider>
      <div class="d-flex gap-8">
        <div>
          <span class="text-caption font-weight-bold">Setor</span>
          <h6 class="mb-0">{{ ticket.TicketBlock.TicketGroup.name }}</h6>
        </div>
        <div>
          <span class="text-caption font-weight-bold">Lote</span>
          <h6 class="mb-0">{{ ticket.TicketBlock.name }}</h6>
        </div>
        <div v-if="ticket.Table">
          <span class="text-caption font-weight-bold">Assento</span>
          <h6 class="mb-0">
            {{ ticket.Table?.Group?.name }} • {{ ticket.Table?.name }}
          </h6>
        </div>
      </div>
    </v-card>

    <v-card v-if="payment" outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Detalhes do pagamento</span>
      <div class="d-flex gap-8">
        <div>
          <span class="text-caption font-weight-bold">Pagamento</span>
          <h6 class="mb-0">{{ payment.id | shortId }}</h6>
        </div>
        <div>
          <span class="text-caption font-weight-bold">Valor</span>
          <h6 class="mb-0">{{ payment.amount | currency }}</h6>
        </div>
        <div>
          <span class="text-caption font-weight-bold">Forma de pagamento</span>
          <h6 class="mb-0">
            {{ PAYMENT.paymentType[payment.paymentType]?.text }}
          </h6>
        </div>
        <div>
          <span class="text-caption font-weight-bold">Meio de pagamento</span>
          <h6 class="mb-0">
            {{ PAYMENT.paymentMethod[payment.paymentMethod]?.text }}
          </h6>
        </div>
      </div>
    </v-card>

    <!-- Comprador -->
    <v-card v-if="payment.Payer" outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Comprador</span>
      <h6 class="mb-0">{{ payment.Payer.name }}</h6>
      <div class="d-flex gap-6">
        <div>
          <div class="">
            <p class="mb-0 lh-1">Telefone</p>
            <p class="mb-0 font-weight-bold">
              {{ payment.Payer.phone }}
            </p>
          </div>
        </div>
        <div>
          <div class="">
            <p class="mb-0 lh-1">Email</p>
            <p class="mb-0 font-weight-bold">
              {{ payment.Payer.email }}
            </p>
          </div>
        </div>
        <div>
          <div class="">
            <p class="mb-0 lh-1">Documento</p>
            <p class="mb-0 font-weight-bold">
              {{ obfuscatedCPF }}
            </p>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Proprietário final -->
    <v-card v-if="ticket.Owner" outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Proprietário final</span>
      <h6 class="mb-0">{{ ticket.Owner.name || "Conta não criada" }}</h6>
      <div class="d-flex gap-6">
        <div>
          <div class="">
            <p class="mb-0 lh-1">Telefone</p>
            <p class="mb-0 font-weight-bold">
              {{ ticket.Owner.phone }}
            </p>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Transferencias -->
    <v-card outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Transferencias</span>
      <v-alert
        v-if="ticket.TicketTransfer.length === 0"
        type="info"
        class="mb-0"
        text
        dense
      >
        Nenhuma transferência desse ingresso
      </v-alert>
      <v-simple-table v-else dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Data</th>
              <th class="text-left">Origem</th>
              <th class="text-left">Destino</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="transfer in ticket.TicketTransfer" :key="transfer.id">
              <td>{{ transfer.createdAt | date("DD/MM/YYYY HH:mm") }}</td>
              <td>
                {{ transfer.from.name || "Conta não criada" }}
                <small class="d-block">{{ transfer.from.phone }}</small>
              </td>
              <td>
                {{ transfer.to.name || "Conta não criada" }}
                <small class="d-block">{{ transfer.to.phone }}</small>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>

    <!-- Acessos -->
    <v-card outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold">Registros de validação</span>
      <v-alert
        v-if="ticket.TicketEntry?.length === 0"
        type="info"
        class="mb-0"
        text
        dense
      >
        Nenhum registro de validação desse ingresso
      </v-alert>
      <v-simple-table v-else dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">ID</th>
              <th class="text-left">Data</th>
              <th class="text-left">Validador</th>
              <th class="text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in ticket.TicketEntry" :key="entry.id">
              <td>{{ entry.id | shortId }}</td>
              <td>{{ entry.createdAt | date("DD/MM/YYYY HH:mm") }}</td>
              <td>
                {{ entry.scannerName }}
              </td>
              <td>
                <v-chip
                  :color="entry.approved ? 'success' : 'error'"
                  label
                  small
                >
                  {{ entry.approved ? "Aprovado" : "Recusado" }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
    <v-card v-if="false" outlined class="mt-3 pa-3 rounded-lg">
      <span class="text-caption font-weight-bold"
        >Solicitações de reembolso</span
      >
      <v-alert
        v-if="ticket.RefoundRequest?.length === 0"
        type="info"
        class="mb-0"
        text
        dense
      >
        Nenhuma solicitação de reembolso desse ingresso
      </v-alert>
      <v-list
        dense
        v-for="refund in ticket.RefoundRequest"
        v-else
        :key="refund.id"
      >
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>{{ refund.id | shortId }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </v-card>
</template>

<script>
import PAYMENT from "@/utils/payment";

export default {
  data: () => ({
    PAYMENT,
  }),
  props: {
    payment: {
      type: Object,
      default: () => null,
    },
    ticket: {
      type: Object,
      default: () => null,
    },
    party: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    obfuscatedCPF() {
      if (!this.payment?.Payer?.document) return "";
      return this.payment.Payer.document
        .replace(/\D/g, "")
        .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.***.***-$4");
    },
    seller() {
      if (this.payment.PosSession)
        return {
          type: this.payment.PosSession.partyId ? "Bilheteria" : "PDV",
          name:
            this.payment.PosSession?.name ||
            this.payment.PosSession?.Address?.name,
        };

      const [ticket] = this.payment?.Ticket || [];
      if (ticket?.Seller) return { type: "Promoter", name: ticket.Seller.name };

      return { type: "Online", name: "Online" };
    },
    saleLink() {
      const ticket = this.payment?.Ticket[0];
      return ticket.SaleLink;
    },
    reducedTickets() {
      const tickets = this.payment?.Ticket || [];
      return tickets.reduce((acc, ticket) => {
        const key = ticket.TicketBlock.id;
        if (!acc[key])
          acc[key] = {
            ...ticket.TicketBlock,
            tickets: [],
          };
        acc[key].tickets.push(ticket);
        return acc;
      }, {});
    },
  },
};
</script>
